import { createSDKI18n, type SDKI18n } from '../../i18n'
import { toXmaxSdkError, XmaxSdkError } from '../../shared/sdk-error'
import {
  MEDIA_TIME_EPSILON_S,
  MEDIA_TIME_LOOP_JUMP_S,
  isMobilePublishEnvironment,
  resolveRtcPublishFrameIntervalS,
  resolveRtcPublishFps,
} from './publish-fps'
import { resolveUploadVideoSize } from './reference-video-size'
import { isAppleMobileRuntime } from './mobile-runtime'
import {
  drawSourceCover,
  type CanvasPosterOptions,
} from '../canvas/canvas-frame-draw'
import { mountCaptureCanvas, mountCaptureVideo } from '../render/capture-host'
import { estimateVideoFrameRate, resolvePlaybackDrivenEncoderFps } from './video-frame-rate'

export type VideoInputHealth = {
  currentTime: number
  duration: number
  paused: boolean
  ended: boolean
  readyState: number
  trackReadyState: string
  trackMuted: boolean
  stalled: boolean
  timeSinceAdvanceMs: number
}

const STALL_CATCHUP_MS = 1500
const MEDIA_READY_TIMEOUT_MS = 8000
const RVFC_STALL_FALLBACK_MS = 400

function isAndroidRuntime(): boolean {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)
}

type VideoFrameCallbackMetadata = {
  mediaTime: number
  presentedFrames: number
}

type VideoWithCaptureStream = HTMLVideoElement & {
  captureStream?: (frameRate?: number) => MediaStream
  mozCaptureStream?: (frameRate?: number) => MediaStream
}

type WindowWithWebkitAudioContext = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext
}

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    cb: (now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => void,
  ) => number
  cancelVideoFrameCallback?: (handle: number) => void
}

export type VideoFileStreamOptions = {
  muted?: boolean
  playbackRate?: number
  /** Optional fps override; when omitted, fps is measured from the source file. */
  fps?: number
  /** Optional hint; publish fps is still derived from the uploaded file when applicable. */
  mobile?: boolean
  targetSize?: [number, number]
  /** Canvas background used for transparent source pixels. @default '#000000' */
  backgroundColor?: string
  /** Still-image poster encoding. Defaults to PNG. */
  poster?: CanvasPosterOptions
  i18n?: SDKI18n
  onHealthReport?: (health: VideoInputHealth) => void
  healthIntervalMs?: number
}

export type VideoFileStream = {
  url: string
  /** Still image matching the composited publish Canvas; available for image sources. */
  posterUrl?: string
  /** Same MediaStream pushed to RTC; bind to preview via srcObject. */
  previewStream: MediaStream
  /** Capture-only element; pinned off-screen. */
  videoEl: HTMLVideoElement
  videoTrack: MediaStreamTrack
  audioTrack?: MediaStreamTrack
  /** Measured source fps — encoder ceiling hint only. */
  fps: number
  /** Session publish dimensions (native source size by default). */
  width: number
  height: number
  /** Original media dimensions before scale. */
  sourceWidth: number
  sourceHeight: number
  destroy: () => void
}

export function shouldSampleAtMediaTime(
  lastMediaTimeS: number,
  mediaTimeS: number,
  frameIntervalS: number,
): boolean {
  if (!Number.isFinite(mediaTimeS) || mediaTimeS < 0) {
    return false
  }
  if (lastMediaTimeS < 0) {
    return true
  }
  if (mediaTimeS + MEDIA_TIME_LOOP_JUMP_S < lastMediaTimeS) {
    return true
  }
  const minIntervalS = Math.max(0, frameIntervalS - MEDIA_TIME_EPSILON_S)
  return mediaTimeS - lastMediaTimeS >= minIntervalS
}

async function applyPublishTrackConstraints(
  track: MediaStreamTrack,
  width: number,
  height: number,
): Promise<void> {
  if (typeof track.applyConstraints !== 'function') {
    return
  }

  try {
    await track.applyConstraints({
      width: { ideal: width, max: width },
      height: { ideal: height, max: height },
    })
  } catch {
    // Some browsers reject exact constraints on captureStream tracks.
  }
}

export async function createVideoFileStream(
  fileOrUrl: Blob | string,
  options: VideoFileStreamOptions = {},
): Promise<VideoFileStream> {
  const i18n = options.i18n ?? createSDKI18n()
  const mobile = isAndroidRuntime() || isAppleMobileRuntime()
    || (options.mobile ?? isMobilePublishEnvironment())
  const isUrl = typeof fileOrUrl === 'string'
  const url = isUrl ? fileOrUrl : URL.createObjectURL(fileOrUrl)
  const videoEl = document.createElement('video')

  videoEl.muted = options.muted ?? true
  videoEl.playsInline = true

  if (isUrl) {
    videoEl.crossOrigin = 'anonymous'
  }
  videoEl.src = url
  videoEl.loop = true
  videoEl.autoplay = true

  mountCaptureVideo(videoEl)

  try {
    videoEl.load()
    await waitForEvent(videoEl, 'loadedmetadata', MEDIA_READY_TIMEOUT_MS)
    // Safari/WebKit often needs canplay before the first frame is drawable.
    // Android can proceed from metadata to avoid hanging on canplay.
    if (!isAndroidRuntime()) {
      await waitForCanPlay(videoEl, MEDIA_READY_TIMEOUT_MS)
    }
  } catch (error) {
    if (!isUrl) URL.revokeObjectURL(url)
    const timedOut = error instanceof Error && /timed out/i.test(error.message)
    throw toXmaxSdkError(error, {
      code: 'MEDIA_PROCESSING_ERROR',
      message: timedOut ? 'Video metadata timed out' : 'Failed to load video metadata',
    })
  }

  if (typeof options.playbackRate === 'number') {
    videoEl.playbackRate = options.playbackRate
  }

  await startVideoPlayback(videoEl, i18n)

  const sourceWidth = videoEl.videoWidth || 0
  const sourceHeight = videoEl.videoHeight || 0
  if (!sourceWidth || !sourceHeight) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.videoTrackMissing'))
  }

  const measuredFps = typeof options.fps === 'number' && options.fps > 0
    ? options.fps
    : mobile ? resolveRtcPublishFps({ mobile: true }) : await estimateVideoFrameRate(videoEl)

  const [outputWidth, outputHeight] = options.targetSize
    ?? resolveUploadVideoSize(sourceWidth, sourceHeight)

  const canvas = document.createElement('canvas')
  canvas.width = outputWidth
  canvas.height = outputHeight
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('UNSUPPORTED_MEDIA', i18n.t('errors.captureStreamUnsupported'))
  }
  const captureStreamFn =
    (canvas as HTMLCanvasElement & { captureStream?: (frameRate?: number) => MediaStream }).captureStream
    ?? (canvas as HTMLCanvasElement & { mozCaptureStream?: (frameRate?: number) => MediaStream }).mozCaptureStream

  if (!captureStreamFn) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('UNSUPPORTED_MEDIA', i18n.t('errors.captureStreamUnsupported'))
  }
  mountCaptureCanvas(canvas)

  const streamFps = resolveRtcPublishFps({ mobile, fps: options.fps })
  // Keep upload video pacing aligned with image/camera publish to avoid over-driving canvas capture.
  const previewStream = captureStreamFn.call(canvas, streamFps)
  const videoTrack = previewStream.getVideoTracks()[0]
  const webAudioCleanupRef: { current: (() => void) | null } = { current: null }
  /**
   * Mobile WebKit / WebView environments frequently fail to sustain external
   * video publish after we force audio extraction from uploaded files.
   * Prefer stable video-only upload publishing on mobile.
   */
  const shouldCaptureUploadAudio = !mobile
  let audioTrack: MediaStreamTrack | undefined
  if (shouldCaptureUploadAudio) {
    audioTrack = await resolveUploadAudioTrack(videoEl, webAudioCleanupRef)
    if (audioTrack && previewStream.getAudioTracks().length === 0) {
      // Playground currently uses `realtime.connect(previewStream, ...)`.
      // Attach audio to the same stream so the generic connect path can pick it up.
      const attachedAudioTrack = typeof audioTrack.clone === 'function' ? audioTrack.clone() : audioTrack
      previewStream.addTrack(attachedAudioTrack)
      audioTrack = attachedAudioTrack
    }
  }

  if (!videoTrack) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.videoTrackMissing'))
  }

  const frameIntervalS = resolveRtcPublishFrameIntervalS({ mobile, fps: streamFps })
  const publishFps = resolvePlaybackDrivenEncoderFps(videoTrack, measuredFps)
  await applyPublishTrackConstraints(videoTrack, outputWidth, outputHeight)

  let destroyed = false
  let lastAdvanceAt = performance.now()
  let lastDrawMediaTimeS = -1
  let lastSampleWallMs = 0
  let videoFrameCallbackId: number | null = null
  let rafId: number | null = null
  let rvfcStallTimer: ReturnType<typeof setTimeout> | null = null
  let usingRvfc = false

  const drawFrame = () => {
    if (destroyed) {
      return
    }
    try {
      drawSourceCover(ctx, videoEl, outputWidth, outputHeight, sourceWidth, sourceHeight, options.backgroundColor)
    } catch {
      // Android may expose metadata before its first drawable frame.
    }
  }

  const onPlaybackSample = (mediaTimeS: number) => {
    if (destroyed || !shouldSampleAtMediaTime(lastDrawMediaTimeS, mediaTimeS, frameIntervalS)) {
      return
    }
    lastDrawMediaTimeS = mediaTimeS
    lastAdvanceAt = performance.now()
    lastSampleWallMs = performance.now()
    drawFrame()
  }

  const scheduleRafWatch = () => {
    if (destroyed || rafId !== null) {
      return
    }
    const tick = () => {
      rafId = null
      if (destroyed) return
      if (!usingRvfc || performance.now() - lastSampleWallMs >= RVFC_STALL_FALLBACK_MS) {
        onPlaybackSample(videoEl.currentTime)
      }
      rafId = window.requestAnimationFrame(tick)
    }
    rafId = window.requestAnimationFrame(tick)
  }

  const scheduleRvfcWatch = () => {
    if (destroyed) return
    const video = videoEl as VideoWithFrameCallback
    if (!video.requestVideoFrameCallback) return
    usingRvfc = true
    videoFrameCallbackId = video.requestVideoFrameCallback!((_now, metadata) => {
      videoFrameCallbackId = null
      onPlaybackSample(metadata.mediaTime)
      if (rvfcStallTimer !== null) clearTimeout(rvfcStallTimer)
      scheduleRvfcWatch()
      rvfcStallTimer = setTimeout(() => {
        usingRvfc = false
        scheduleRafWatch()
      }, RVFC_STALL_FALLBACK_MS)
    })
  }

  const schedulePlaybackWatch = () => {
    scheduleRafWatch()
    if (!mobile) scheduleRvfcWatch()
  }

  drawFrame()
  schedulePlaybackWatch()

  const ensurePlaying = () => {
    if (destroyed) {
      return
    }
    if (videoEl.ended && videoEl.loop) {
      videoEl.currentTime = 0
      lastDrawMediaTimeS = -1
    }
    if (videoEl.paused) {
      void videoEl.play().catch(() => undefined)
    }
  }

  videoEl.addEventListener('pause', ensurePlaying)
  videoEl.addEventListener('ended', ensurePlaying)
  videoEl.addEventListener('stalled', ensurePlaying)
  videoEl.addEventListener('waiting', ensurePlaying)

  const healthIntervalMs = options.healthIntervalMs ?? 3000
  let healthTimer: ReturnType<typeof setInterval> | null = null

  if (options.onHealthReport) {
    const reportHealth = options.onHealthReport
    healthTimer = setInterval(() => {
      ensurePlaying()

      const now = performance.now()
      const currentTime = videoEl.currentTime
      const duration = videoEl.duration
      const nearEnd = duration > 0 && currentTime >= duration - 0.2
      const stalled = !videoEl.paused && !videoEl.ended && !nearEnd && now - lastAdvanceAt >= healthIntervalMs

      if (stalled && now - lastAdvanceAt >= STALL_CATCHUP_MS) {
        void videoEl.play().catch(() => undefined)
      }

      reportHealth({
        currentTime,
        duration,
        paused: videoEl.paused,
        ended: videoEl.ended,
        readyState: videoEl.readyState,
        trackReadyState: videoTrack.readyState,
        trackMuted: videoTrack.muted,
        stalled,
        timeSinceAdvanceMs: now - lastAdvanceAt,
      })
    }, healthIntervalMs)
  }

  const cancelPlaybackWatch = () => {
    const video = videoEl as VideoWithFrameCallback
    if (rvfcStallTimer !== null) clearTimeout(rvfcStallTimer)
    if (videoFrameCallbackId !== null) {
      video.cancelVideoFrameCallback?.(videoFrameCallbackId)
      videoFrameCallbackId = null
    }
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  const destroy = () => {
    destroyed = true
    videoEl.removeEventListener('pause', ensurePlaying)
    videoEl.removeEventListener('ended', ensurePlaying)
    videoEl.removeEventListener('stalled', ensurePlaying)
    videoEl.removeEventListener('waiting', ensurePlaying)

    cancelPlaybackWatch()

    if (healthTimer !== null) {
      clearInterval(healthTimer)
      healthTimer = null
    }

    try {
      videoEl.pause()
    } catch {
    }

    try {
      videoTrack.stop()
    } catch {
    }

    try {
      previewStream.getTracks().forEach((track) => {
        if (track.readyState !== 'ended') {
          track.stop()
        }
      })
    } catch {
    }
    try {
      webAudioCleanupRef.current?.()
      webAudioCleanupRef.current = null
    } catch {
    }

    try {
      videoEl.remove()
    } catch {
    }

    try {
      canvas.remove()
    } catch {
    }

    try {
      videoEl.src = ''
      videoEl.load()
    } catch {
    }

    if (!isUrl) URL.revokeObjectURL(url)
  }

  return {
    url,
    previewStream,
    videoEl,
    videoTrack,
    audioTrack,
    fps: publishFps,
    width: outputWidth,
    height: outputHeight,
    sourceWidth,
    sourceHeight,
    destroy,
  }
}

async function resolveUploadAudioTrack(
  videoEl: HTMLVideoElement,
  webAudioCleanupRef: { current: (() => void) | null },
): Promise<MediaStreamTrack | undefined> {
  let audioTrack = getFirstLiveAudioTrack(getVideoElementStream(videoEl))
  if (audioTrack) {
    return audioTrack
  }

  // Some browsers start playback only when muted, but muted media elements may expose
  // no capturable audio track. Switch to silent (volume=0) playback and retry capture.
  ensureSilentButUnmuted(videoEl)
  audioTrack = getFirstLiveAudioTrack(getVideoElementStream(videoEl))
  if (audioTrack) {
    return audioTrack
  }

  // Safari fallback: derive an external audio track via WebAudio when captureStream
  // cannot provide audio tracks for file-based video playback.
  const webAudio = await createAudioTrackViaWebAudio(videoEl)
  if (webAudio) {
    webAudioCleanupRef.current = webAudio.cleanup
    return webAudio.track
  }

  return undefined
}

async function createAudioTrackViaWebAudio(videoEl: HTMLVideoElement): Promise<{
  track: MediaStreamTrack
  cleanup: () => void
} | null> {
  const AudioContextCtor = globalThis.AudioContext
    ?? (globalThis.window as WindowWithWebkitAudioContext | undefined)?.webkitAudioContext
  if (!AudioContextCtor) {
    return null
  }

  let audioContext: AudioContext | null = null
  let source: MediaElementAudioSourceNode | null = null
  let destination: MediaStreamAudioDestinationNode | null = null
  try {
    audioContext = new AudioContextCtor()
    source = audioContext.createMediaElementSource(videoEl)
    destination = audioContext.createMediaStreamDestination()
    source.connect(destination)

    if (audioContext.state === 'suspended') {
      await audioContext.resume()
    }

    const track = getFirstLiveAudioTrack(destination.stream)
    if (!track) {
      source.disconnect()
      destination.disconnect()
      await audioContext.close()
      return null
    }

    const cleanup = () => {
      try {
        source?.disconnect()
      } catch {
      }
      try {
        destination?.disconnect()
      } catch {
      }
      try {
        if (track.readyState !== 'ended') {
          track.stop()
        }
      } catch {
      }
      try {
        if (audioContext && audioContext.state !== 'closed') {
          void audioContext.close()
        }
      } catch {
      }
    }

    return { track, cleanup }
  } catch {
    try {
      source?.disconnect()
    } catch {
    }
    try {
      destination?.disconnect()
    } catch {
    }
    try {
      if (audioContext && audioContext.state !== 'closed') {
        await audioContext.close()
      }
    } catch {
    }
    return null
  }
}

function getVideoElementStream(videoEl: HTMLVideoElement): MediaStream | null {
  const captureVideoEl = videoEl as VideoWithCaptureStream
  const capture = captureVideoEl.captureStream ?? captureVideoEl.mozCaptureStream
  if (!capture) {
    return null
  }
  try {
    return capture.call(captureVideoEl)
  } catch {
    return null
  }
}

function getFirstLiveAudioTrack(stream: MediaStream | null): MediaStreamTrack | undefined {
  return stream?.getAudioTracks().find((track) => track.readyState === 'live')
}

function ensureSilentButUnmuted(videoEl: HTMLVideoElement): void {
  videoEl.muted = false
  videoEl.volume = 0
}

function waitForEvent<T extends HTMLElement>(
  el: T,
  eventName: keyof HTMLElementEventMap,
  timeoutMs: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const onOk = () => {
      cleanup()
      resolve()
    }

    const onErr = (e: Event) => {
      cleanup()
      // If it's a media error and we can read the code, include it
      const mediaEl = el as unknown as HTMLMediaElement
      const code = mediaEl.error ? mediaEl.error.code : 'unknown'
      const message = mediaEl.error ? mediaEl.error.message : 'no message'
      const src = mediaEl.src || 'unknown src'
      reject(new Error(`failed to load video metadata: ${String(eventName)} (code: ${code}, message: ${message}, src: ${src})`))
    }

    const onTimeout = () => {
      cleanup()
      reject(new Error(`timed out waiting for video ${String(eventName)}`))
    }

    const cleanup = () => {
      clearTimeout(timeoutId)
      el.removeEventListener(eventName, onOk as EventListener)
      el.removeEventListener('error', onErr as EventListener)
    }

    const timeoutId = setTimeout(onTimeout, timeoutMs)
    el.addEventListener(eventName, onOk as EventListener, { once: true })
    el.addEventListener('error', onErr as EventListener, { once: true })
  })
}

function waitForCanPlay(videoEl: HTMLVideoElement, timeoutMs: number): Promise<void> {
  if (videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const onReady = () => {
      cleanup()
      resolve()
    }

    const onTimeout = () => {
      cleanup()
      reject(new Error('timed out waiting for video canplay'))
    }

    const cleanup = () => {
      clearTimeout(timeoutId)
      videoEl.removeEventListener('canplay', onReady)
      videoEl.removeEventListener('loadeddata', onReady)
    }

    const timeoutId = setTimeout(onTimeout, timeoutMs)
    videoEl.addEventListener('canplay', onReady, { once: true })
    videoEl.addEventListener('loadeddata', onReady, { once: true })
  })
}

async function startVideoPlayback(videoEl: HTMLVideoElement, i18n: SDKI18n): Promise<void> {
  try {
    const playResult = videoEl.play()
    if (playResult && typeof (playResult as Promise<void>).then === 'function') {
      await playResult
    }
  } catch {
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.videoPlaybackFailed'))
  }

  try {
    await waitForPlaying(videoEl, MEDIA_READY_TIMEOUT_MS)
  } catch {
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.videoPlaybackFailed'))
  }

  if (videoEl.paused || videoEl.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.videoPlaybackFailed'))
  }
}

function waitForPlaying(videoEl: HTMLVideoElement, timeoutMs: number): Promise<void> {
  if (!videoEl.paused && videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const onPlaying = () => {
      cleanup()
      resolve()
    }

    const onErr = () => {
      cleanup()
      reject(new Error('video playback failed'))
    }

    const onTimeout = () => {
      cleanup()
      reject(new Error('timed out waiting for video playback'))
    }

    const cleanup = () => {
      clearTimeout(timeoutId)
      videoEl.removeEventListener('playing', onPlaying)
      videoEl.removeEventListener('error', onErr)
    }

    const timeoutId = setTimeout(onTimeout, timeoutMs)
    videoEl.addEventListener('playing', onPlaying, { once: true })
    videoEl.addEventListener('error', onErr, { once: true })
  })
}
